import React, { useState, useEffect, useContext, useMemo } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { LayoutDashboard, Briefcase, BarChart2, Video, PlusSquare, CreditCard, HelpCircle, LogOut, FileText, Tag, User, Users, MessageSquare, UserPlus, Activity } from 'lucide-react';
import { AuthContext } from '../../context/AuthContext';
import useResponsive from '../../hooks/useResponsive';

const Sidebar = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [isCollapsed, setIsCollapsed] = useState(window.innerWidth < 1024);
  const { user, logout } = useContext(AuthContext);
  const [userLoading, setUserLoading] = useState(!user);
  
  // Handle missing user type with fallback
  const userType = user?.userType;
  
  const isEmployer = userType === 'employer';
  const isRecruiter = userType === 'recruiter';
  
  // Update loading state when user becomes available
  useEffect(() => {
    if (user) {
      setUserLoading(false);
    }
  }, [user]);
  
  // Handle window resize for responsive sidebar
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 768) {
        setIsCollapsed(true);
      }
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Memoize base path calculation to prevent unnecessary recalculations
  const basePath = useMemo(() => {
    return (user && userType === 'recruiter') ? '/recruiter' : '';
  }, [user, userType]);
  
  // Show loading skeleton while user data is loading
  if (userLoading) {
    return (
      <aside className="w-24 bg-white border-r border-gray-200 flex flex-col items-center py-6 shadow-sm">
        <div className="animate-pulse space-y-4 w-full px-4">
          <div className="h-8 bg-gray-200 rounded"></div>
          <div className="h-8 bg-gray-200 rounded"></div>
          <div className="h-8 bg-gray-200 rounded"></div>
          <div className="h-8 bg-gray-200 rounded"></div>
        </div>
      </aside>
    );
  }
  
  const navItems = {
    main: [
      { icon: <LayoutDashboard size={20} />, name: 'Dashboard', path: `${basePath}/dashboard` },
      { icon: <MessageSquare size={20} />, name: 'Inbox', path: `${basePath}/inbox` },
    ],
    applications: [
      { icon: <FileText size={20} />, name: 'All Applicants', path: `${basePath}/applications` },
    ],
    talent: [
      { icon: <Users size={20} />, name: 'Database', path: `${basePath}/database` },
      { icon: <Briefcase size={20} />, name: 'Posted Jobs', path: `${basePath}/posted-jobs` },
      { icon: <PlusSquare size={20} />, name: 'Post Job', path: `${basePath}/post-job` },
      // Interviews removed
    ],
    company: [
      { icon: <BarChart2 size={20} />, name: 'Analytics', path: '/analytics' },
      // DEI Dashboard removed
      { icon: <Video size={20} />, name: 'Branding', path: '/branding' },
    ],
    admin: [
      { icon: <CreditCard size={20} />, name: 'Billing', path: '/billing' },
      { icon: <Tag size={20} />, name: 'Pricing', path: '/pricing' },
      { icon: <HelpCircle size={20} />, name: 'Support', path: `${basePath}/support` },
    ],
    recruiterManagement: [
      { icon: <UserPlus size={20} />, name: 'Manage Recruiters', path: '/recruiters' },
      { icon: <Activity size={20} />, name: 'Recruiter Performance', path: '/recruiter-performance' },
    ]};

  const NavItem = ({ icon, name, path }) => {
    const isActive = location.pathname === path;
    return (
      <Link
        to={path}
        title={name}
        className={`group relative flex items-center justify-center py-1.5 my-0.5 transition-all duration-200 ${isActive ? 'text-black font-medium' : 'text-gray-500 hover:text-black'}`}
      >
        {React.cloneElement(icon, { size: 20, className: 'text-current' })}
      </Link>
    );
  };

  const NavSection = ({ items }) => (
    <div className="mb-2">
      <div>
        {items && items.length > 0 ? (
          items.map((item, index) => (
            <NavItem key={index} {...item} />
          ))
        ) : null}
      </div>
    </div>
  );

  const { isMobile } = useResponsive();
  
  // Don't render the sidebar on mobile
  if (isMobile) return null;
  
  return (
    <aside
      className="flex flex-col h-[calc(70vh-2rem)] bg-white shadow-[0_10px_30px_-5px_rgba(0,0,0,0.15),_0_5px_15px_-3px_rgba(0,0,0,0.08)] w-14 z-20 rounded-3xl mx-4 fixed top-1/2 left-4 transform -translate-y-1/2"
      style={{ boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)' }}
    >
      {/* Sidebar Header */}
      <div className="flex items-center justify-center h-10 mb-1 mt-1">
        <Link to="/dashboard" className="flex items-center justify-center w-full">
          <span className="text-lg font-bold text-black">
            J
          </span>
        </Link>
      </div>

      {/* Navigation */}
      <nav className="flex-1 flex flex-col items-center py-2 overflow-y-auto">
        <NavSection items={navItems.main} />
        <NavSection items={navItems.applications} />
        <NavSection items={navItems.talent.slice(0, 2)} />
        
        {/* Show Post Job to both employers and recruiters */}
        <NavSection items={[navItems.talent[2]]} /> {/* Post Job */}
        
        <div className="w-5 h-[1px] bg-gray-300 my-2"></div>
        
        {/* Only show billing and pricing to employers */}
        {!isRecruiter && (
          <>
            <NavSection items={[navItems.admin[0]]} /> {/* Billing */}
            <NavSection items={[navItems.admin[1]]} /> {/* Pricing */}
          </>
        )}
        
        <NavSection items={[navItems.admin[2]]} /> {/* Support */}
        
        {/* Show recruiter management links only to employers */}
        {isEmployer && (
          <>
            <div className="w-5 h-[1px] bg-gray-300 my-2"></div>
            <NavSection items={navItems.recruiterManagement} />
          </>
        )}
      </nav>

      {/* User Profile */}
      <div className="py-1 mt-auto mb-1">        
        <div className="w-5 h-[1px] bg-gray-300 mb-1 mx-auto"></div>
        <Link
          to={`${basePath}/profile`}
          title="Profile"
          className="flex items-center justify-center py-1.5 my-0.5 transition-all duration-200 text-gray-500 hover:text-black"
        >
          <User size={20} className="text-current" />
        </Link>
        
        <button
          onClick={async () => {
            try {
              await logout();
              navigate('/');
            } catch (err) {
              console.error('Logout failed:', err);
              // Clear local storage manually if API logout fails
              localStorage.clear();
              // Always navigate to home page even if logout fails
              navigate('/');
            }
          }}
          title="Logout"
          className="flex items-center justify-center py-1.5 my-0.5 transition-all duration-200 text-gray-500 hover:text-black w-full"
        >
          <LogOut size={20} className="text-current" />
        </button>
      </div>
    </aside>
  );
};

export default Sidebar;
